import { useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";

import { IconNotification } from "@/components/notifications/notifications";
import {
	initialSettingsState,
	settingsActions,
	SettingsDispatchContext,
	settingsReducer,
	type SettingsState,
	SettingsStateContext,
} from "@/context/settings";
import { ColorThemeModal } from "@/features/modals/color-theme-modal";
import { useReducerWithLogger } from "@/hooks/useReducerWithLogger";
import { ContrastModes, SettingsProviderProps } from "@/types/settings";

/**
 * SettingsProvider component that manages settings state using useReducer pattern.
 *
 * This provider:
 * - Uses useReducer for predictable state management
 * - Separates state and dispatch contexts for better performance
 * - Handles theme application and localStorage persistence
 * - Provides backward-compatible API through useSettings hook
 */
export const SettingsProvider = ({
	children,
	darkModeClass = "dark-mode",
	defaultContrastMode = ContrastModes.SYSTEM,
	storageKey = "atlas-devops-theme",
}: SettingsProviderProps) => {
	// Initialize state from localStorage or default
	const [state, dispatch] = useReducerWithLogger(
		settingsReducer,
		initialSettingsState,
		(initialState): SettingsState => {
			if (typeof window !== "undefined") {
				const savedContrastMode = localStorage.getItem(
					storageKey,
				) as ContrastModes | null;
				return {
					...initialState,
					contrastMode: savedContrastMode || defaultContrastMode,
				};
			}
			return {
				...initialState,
				contrastMode: defaultContrastMode,
			};
		},
		import.meta.env.DEV, // Enable logging in development mode
	);

	const prevContrastModeRef = useRef<ContrastModes | null>(null);

	const handleNotification = () => {
		toast.custom((t) => {
			return (
				<IconNotification
					color="success"
					hideDismissLabel={true}
					onClose={() => toast.dismiss(t)}
					title="Theme saved successfully"
				/>
			);
		});
	};

	// Apply theme changes to DOM and localStorage
	useEffect(() => {
		const applyTheme = () => {
			const root = window.document.documentElement;

			if (state.contrastMode === ContrastModes.SYSTEM) {
				const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
					.matches
					? ContrastModes.DARK
					: ContrastModes.LIGHT;

				root.classList.toggle(
					darkModeClass,
					systemTheme === ContrastModes.DARK,
				);
				localStorage.removeItem(storageKey);
			} else {
				root.classList.toggle(
					darkModeClass,
					state.contrastMode === ContrastModes.DARK,
				);
				localStorage.setItem(storageKey, state.contrastMode);
			}
		};

		// Only show notification if theme actually changed (not on initial mount)
		if (
			prevContrastModeRef.current !== null &&
			prevContrastModeRef.current !== state.contrastMode
		) {
			handleNotification();
		}

		applyTheme();
		prevContrastModeRef.current = state.contrastMode;

		// Listen for system theme changes
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

		const handleChange = () => {
			if (state.contrastMode === ContrastModes.SYSTEM) {
				applyTheme();
			}
		};

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [state.contrastMode, darkModeClass, storageKey]);

	// Memoize dispatch to prevent unnecessary re-renders
	// dispatch from useReducer is stable, so this memoization is safe
	const memoizedDispatch = useMemo(() => dispatch, [dispatch]);

	return (
		<SettingsStateContext.Provider value={state}>
			<SettingsDispatchContext.Provider value={memoizedDispatch}>
				{children}
				<ColorThemeModal
					isOpen={state.isSettingsModalOpen}
					onClose={() =>
						dispatch(settingsActions.updateSettingsModalVisibility(false))
					}
				/>
			</SettingsDispatchContext.Provider>
		</SettingsStateContext.Provider>
	);
};
